/**
 * PostHog product analytics
 *
 * Off unless VITE_POSTHOG_KEY is set, so local and any environment without a
 * key stays silent.
 *
 * Autocapture is on but session replay is not. A replay of this app is a
 * recording of someone's calendar, so turning it on is a privacy decision for
 * a human to make, not a default.
 *
 * @link https://posthog.com/docs/libraries/vue-js
 */

import posthog from 'posthog-js'
import type { App } from 'vue'
import type { Router } from 'vue-router'

const key  = import.meta.env.VITE_POSTHOG_KEY
const host = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com'

export const enabled = !!key

/** Query params the login flow puts in the URL that must never leave the browser. */
const SENSITIVE_PARAMS = ['phone', 'otp', 'code', 'token', 'email']

/** Keeps the path, drops anything identifying from the query string. */
export function scrubUrl(url: string): string {
    if (!url) return url

    const [path, query] = url.split('?')
    if (!query) return url

    const params = new URLSearchParams(query)
    SENSITIVE_PARAMS.forEach(p => params.has(p) && params.set(p, 'redacted'))

    const rest = params.toString()

    return rest ? `${path}?${rest}` : path
}

export default {
    install(app: App) {
        posthog.init(key, {
            api_host: host,
            // The SPA only loads once, so PostHog's own pageview listener sees a
            // single view. The router hook below sends the rest.
            capture_pageview: false,
            autocapture: true,
            disable_session_recording: true,
            // Event titles and member names end up in the DOM, so never let
            // autocapture read element text.
            mask_all_element_attributes: true,
            mask_all_text: true,
            // Autocapture reads the address bar itself, and the OTP screen
            // carries the phone number there. Catch every URL on the way out.
            sanitize_properties: (properties) => {
                ['$current_url', '$referrer', '$pathname'].forEach((k) => {
                    if (typeof properties[k] === 'string') {
                        properties[k] = scrubUrl(properties[k] as string)
                    }
                })

                return properties
            },
        })

        let lastPath: string | null = null

        // Taken off the app rather than imported: the user store imports this
        // file, and the router reaches that store through its middleware. A
        // static import here would close that loop.
        const router = app.config.globalProperties.$router as Router

        router.afterEach((to) => {
            // Guards redirect through the same path more than once, and each hop
            // lands here. Only the arrival is a pageview.
            if (to.fullPath === lastPath) return
            lastPath = to.fullPath

            posthog.capture('$pageview', { $current_url: to.fullPath })
        })

        app.config.globalProperties.$posthog = posthog
    },
}

/**
 * Tie events to the account rather than the browser. Id only: a phone number
 * or an email sent to a third party cannot be taken back.
 */
export function identify(user: { id: number; role?: string; account_id?: number }): void {
    if (!enabled) return

    posthog.identify(String(user.id), {
        role: user.role,
        account_id: user.account_id,
    })
}

export function reset(): void {
    if (!enabled) return

    posthog.reset()
}

/** Named events, sent alongside the GTM push so both tools see the same moment. */
export function capture(event: string, properties?: Record<string, unknown>): void {
    if (!enabled) return

    posthog.capture(event, properties)
}
